import { Wrench, Trash2, Leaf, Home, HeartHandshake, HelpCircle, ShieldAlert } from "lucide-react";
import { TextInput, TextArea, SelectInput, Choice, Check } from "./fields";

export const SERVICE_OPTIONS = [
  ["handyman", "Handyman", Wrench],
  ["junk", "Junk removal", Trash2],
  ["yard", "Yard cleanup", Leaf],
  ["cleanout", "Property cleanout", Home],
  ["senior", "Senior home support", HeartHandshake],
  ["not_sure", "Not sure", HelpCircle],
];
export const SERVICE_LABEL = Object.fromEntries(SERVICE_OPTIONS.map(([k, l]) => [k, l]));
const YES_NO = [["yes", "Yes"], ["no", "No"]];

export function StepServices({ form, setForm, errors }) {
  const toggle = (k) => setForm((f) => ({ ...f, services: f.services.includes(k) ? f.services.filter((s) => s !== k) : [...f.services, k] }));
  return (
    <fieldset id="services" tabIndex={-1} aria-describedby={errors.services ? "services-error" : undefined}>
      <legend className="text-2xl font-bold">What can we help with? <span className="text-base font-normal text-neutral-600">Choose all that apply.</span></legend>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {SERVICE_OPTIONS.map(([k, l, Icon]) => {
          const on = form.services.includes(k);
          return (
            <label key={k} className={`flex min-h-[72px] cursor-pointer items-center gap-4 rounded-lg border-2 p-4 text-lg ${on ? "border-[#D9232E] bg-red-50 font-bold" : "border-neutral-300 bg-white hover:border-neutral-500"}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(k)} className="h-6 w-6 accent-[#D9232E]" data-testid={`service-option-${k}`} />
              <Icon className="h-7 w-7 text-[#D9232E]" aria-hidden="true" />{l}
            </label>
          );
        })}
      </div>
      {errors.services && <p id="services-error" className="field-error" role="alert">{errors.services}</p>}
    </fieldset>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className="mb-8 rounded-lg border border-neutral-200 bg-white p-5 sm:p-6" data-testid={testid}>
      <h3 className="mb-4 text-2xl font-bold uppercase">{title}</h3>
      {children}
    </section>
  );
}

export function StepDetails({ form, setDetail, errors, site }) {
  const d = form.details;
  const s = form.services;
  const tiers = (site.load_tiers || []).map((t) => [t.key, t.label]);
  const amountOpts = [...tiers, ["more", "More than a full truckload"], ["unsure", "Not sure"]];
  const handyTasks = (site.services || []).filter((x) => x.category === "handyman").map((x) => [x.key, x.name]);
  const f = (svc, key) => ({ id: `${svc}_${key}`, value: d[svc]?.[key], onChange: (v) => setDetail(svc, key, v), error: errors[`${svc}_${key}`] });
  return (
    <div>
      <p className="mb-6 flex gap-3 rounded-md border-2 border-amber-600 bg-amber-50 p-4 text-base text-amber-900" role="note">
        <ShieldAlert className="h-6 w-6 shrink-0" aria-hidden="true" />Please don't include payment-card details, ID documents, or gate, lock or alarm codes in any answer.
      </p>
      {s.includes("junk") && (
        <Section title="Junk removal" testid="details-junk">
          <TextArea {...f("junk", "items")} label="What items, and how many?" required hint="Example: 1 couch, 2 mattresses, about 10 boxes." />
          <Choice {...f("junk", "amount")} label="About how much is there?" required options={amountOpts} />
          <Choice {...f("junk", "location")} label="Where are the items?" required options={[["indoors", "Indoors"], ["garage", "Garage"], ["yard", "Yard"], ["curbside", "Curbside"]]} />
          <SelectInput {...f("junk", "stairs_flights")} label="Flights of stairs to carry items" options={[["0", "No stairs"], ["1", "1 flight"], ["2", "2 flights"], ["3", "3 or more flights"]]} />
          <Choice {...f("junk", "elevator")} label="Is there an elevator?" options={[["yes", "Yes"], ["no", "No"], ["na", "Not applicable"]]} />
          <Choice {...f("junk", "carry_distance")} label="How far from where the truck parks?" options={[["short", "Short (under 50 ft)"], ["medium", "Medium (50–150 ft)"], ["long", "Long (over 150 ft)"], ["unsure", "Not sure"]]} />
          <Check {...f("junk", "disassembly")} checked={d.junk?.disassembly} label="Some items need to be taken apart" />
          <Check {...f("junk", "heavy_items")} checked={d.junk?.heavy_items} label="Unusually heavy items (piano, safe, hot tub, concrete, etc.)" />
          <Choice {...f("junk", "appliances")} label="Appliances" options={[["none", "No appliances"], ["disconnected", "Already disconnected"], ["not_disconnected", "Still connected"]]} hint="We can only remove appliances that are already disconnected." />
          <Check {...f("junk", "hazards")} checked={d.junk?.hazards} label="Special materials or possible hazards" desc="Paint, chemicals, fuel, batteries, possible asbestos, medical waste, etc." />
          {d.junk?.hazards && <TextArea {...f("junk", "hazard_notes")} label="Describe the special materials" />}
        </Section>
      )}
      {s.includes("handyman") && (
        <Section title="Handyman" testid="details-handyman">
          <SelectInput {...f("handyman", "task_type")} label="Type of task" required options={[...handyTasks, ["other", "Other small task (we'll confirm it's in our scope)"]]} />
          <TextArea {...f("handyman", "description")} label="Describe the job" required />
          <TextInput {...f("handyman", "quantity")} label="Quantity or dimensions" hint="Example: 3 cabinet doors, or a 4 ft shelf." />
          <TextInput {...f("handyman", "condition")} label="Existing condition" />
          <Choice {...f("handyman", "materials")} label="Materials" options={[["customer", "I have the materials"], ["one_punch", "Please bring materials"], ["unsure", "Not sure"]]} />
          <TextInput {...f("handyman", "access")} label="Access considerations" hint="Ladders, tight spaces, pets, parking — no lock codes please." />
          <Check {...f("handyman", "restricted_trade")} checked={d.handyman?.restricted_trade} label="This might involve electrical, plumbing, gas, HVAC or structural work" desc="We don't currently offer regulated trade work, but we'll review your request and let you know." />
        </Section>
      )}
      {s.includes("cleanout") && (
        <Section title="Property cleanout" testid="details-cleanout">
          <Choice {...f("cleanout", "property_type")} label="Property type" required options={[["house", "House"], ["apartment", "Apartment / condo"], ["rental", "Rental property"], ["mobile", "Mobile home"], ["other", "Other"]]} />
          <TextArea {...f("cleanout", "areas")} label="Rooms or areas to clear" required />
          <TextArea {...f("cleanout", "keep_donate_remove")} label="Items to keep, donate or remove" />
          <TextInput {...f("cleanout", "completion_date")} type="date" label="Requested completion date" />
          <TextInput {...f("cleanout", "authorized_person")} label="Who is authorized to approve removal?" required hint="Name and role, e.g. owner, executor or property manager." />
        </Section>
      )}
      {s.includes("yard") && (
        <Section title="Yard cleanup" testid="details-yard">
          <Choice {...f("yard", "area")} label="Approximate area" required options={[["small", "Small (part of a yard)"], ["medium", "Medium (front or back yard)"], ["large", "Large (whole property)"], ["unsure", "Not sure"]]} />
          <TextInput {...f("yard", "debris_type")} label="Type of debris" hint="Leaves, weeds, branches, bagged green waste…" />
          <Choice {...f("yard", "amount")} label="About how much debris?" options={amountOpts} />
          <TextInput {...f("yard", "access")} label="Gate or access limitations" hint="Gate width, slopes, steps — no gate codes please." />
          <Choice {...f("yard", "service_type")} label="What do you need?" required options={[["loading_only", "Loading & hauling only (already piled)"], ["cleanup_and_hauling", "Cleanup and hauling"]]} />
        </Section>
      )}
      {s.includes("senior") && (
        <Section title="Senior home support" testid="details-senior">
          <TextArea {...f("senior", "needs")} label="What would you like help with?" required hint="Decluttering, downsizing, a list of small tasks — in your own words." />
        </Section>
      )}
      {s.includes("not_sure") && (
        <Section title="Tell us about it" testid="details-not-sure">
          <TextArea {...f("other", "notes")} label="Describe what you need in your own words" required />
        </Section>
      )}
    </div>
  );
}

export function validateDetails(form) {
  const e = {};
  const d = form.details;
  const req = (svc, key, msg) => { if (!String(d[svc]?.[key] ?? "").trim()) e[`${svc}_${key}`] = msg; };
  const s = form.services;
  if (s.includes("junk")) { req("junk", "items", "Please list the items."); req("junk", "amount", "Please choose an amount (or Not sure)."); req("junk", "location", "Please choose where the items are."); }
  if (s.includes("handyman")) { req("handyman", "task_type", "Please choose a task type."); req("handyman", "description", "Please describe the job."); }
  if (s.includes("cleanout")) { req("cleanout", "property_type", "Please choose a property type."); req("cleanout", "areas", "Please list the rooms or areas."); req("cleanout", "authorized_person", "Please tell us who can approve removal."); }
  if (s.includes("yard")) { req("yard", "area", "Please choose an approximate area."); req("yard", "service_type", "Please choose what you need."); }
  if (s.includes("senior")) req("senior", "needs", "Please tell us what you'd like help with.");
  if (s.includes("not_sure")) req("other", "notes", "Please describe what you need.");
  Object.entries(d).forEach(([svc, vals]) => Object.entries(vals || {}).forEach(([k, v]) => {
    if (typeof v === "string" && /\b(?:\d[ -]?){13,19}\b/.test(v)) e[`${svc}_${k}`] = "This looks like a card or account number. Please remove it — we never need payment details here.";
  }));
  return e;
}

export { YES_NO };
